import PluginAction from '../plugin-action';
import LanguageUtility from '../../utilities/language-utility';

/**
 * Represents a selectable sport.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
type Sport = {
    uid: number,
    title: string
};

/**
 * Represents a selected sport.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
type SelectedSport = {
    uid: number, // UID of the selected sport
    title: string,

    // Optional sport options
    skillLevel: string | null,
    rentalEquipment: boolean | null
};

/**
 * Action showing the form for accepting a student registration.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class AcceptAction extends PluginAction {
    /**
     * All available sport options.
     */
    private sportOptions: NodeListOf<HTMLInputElement>;

    /**
     * Hidden field containing the selected sports.
     */
    private sportsField: HTMLInputElement;

    constructor(container: HTMLElement) {
        super(container);
        const sportOptions = container.querySelectorAll<HTMLInputElement>('input[name="tx_sitepackage_studentregistration[sportOption][]"]');
        const sportsField = container.querySelector<HTMLInputElement>('input[name="tx_sitepackage_studentregistration[registration][sports]"]');
        if (sportOptions.length === 0 || !sportsField) {
            throw new Error('Required elements missing.');
        }
        this.sportOptions = sportOptions;
        this.sportsField = sportsField;
        this.initializeSportOptions();
    }

    /**
     * Initializes the sport options by enriching them with a selectable skill level and such.
     */
    private initializeSportOptions(): void {
        this.sportOptions.forEach(option => {
            option.addEventListener('change', () => {
                if (option.checked) {
                    this.selectSportOption(option);
                } else {
                    this.deselectSportOption(option);
                }
            });
        });

        // Setup previously chosen sports and their additional options
        if (this.sportsField.value) {
            const selectedSports: SelectedSport[] = JSON.parse(this.sportsField.value);
            for (const selectedSport of selectedSports) {
                this.sportOptions.forEach(option => {
                    if (selectedSport.uid === parseInt(option.value)) {
                        this.selectSportOption(option, selectedSport);
                        return;
                    }
                });
            }
        }
    }

    /**
     * Selects a sport option.
     *
     * @param {HTMLInputElement} option Sport option to select
     * @param {SelectedSport} [selectedSport] Initialize with a previously selected sport, if needed (defaults to nothing)
     */
    private selectSportOption(option: HTMLInputElement, selectedSport: SelectedSport | null = null): void {
        if (!option.parentElement) {
            throw new Error('Required elements missing.');
        }
        // Ignore for the default "no specific sport selected" option
        if (option.value === '0') {
            // Still update the hidden field, as an empty array will hint that no actual sport is selected
            this.updateHiddenSportsField();
            return;
        }

        // Create additional options for a sport
        let additionalOptions = '';
        if (option.dataset.skillLevelEnabled === '1') {
            additionalOptions += `
            <div class="col-12 col-md-6">
        <div class="form-floating">
            <select class="form-select" id="${option.id}-skill-level" name="${option.id}-skill-level" data-skill-level>
                <option value="${LanguageUtility.getLabel('skill.advanced')}">${LanguageUtility.getLabel('skill.advanced')}</option>
                <option value="${LanguageUtility.getLabel('skill.slightlyAdvanced')}">${LanguageUtility.getLabel('skill.slightlyAdvanced')}</option>
                <option value="${LanguageUtility.getLabel('skill.rookie')}">${LanguageUtility.getLabel('skill.rookie')}</option>
            </select>
            <label for="${option.id}-skill-level">${LanguageUtility.getLabel('mySkillLevel')}</label>
        </div>
    </div>
`;
        }

        if (option.dataset.rentalEquipmentEnabled === '1') {
            additionalOptions += `
            <div class="col-12 col-md-6 d-flex align-items-center">
        <div class="form-check">
            <input
                class="form-check-input"
                id="${option.id}-rental-equipment"
                type="checkbox"
                name="${option.id}-rental-equipment"
                value="1"
                data-rental-equipment
            >
            <label for="${option.id}-rental-equipment" class="form-check-label">${LanguageUtility.getLabel('rentalEquipmentRequired')}</label>
        </div>
    </div>
`;
        }

        if (additionalOptions) {
            additionalOptions = `<div class="row ms-5 mt-n3" data-additional-sport-options data-sport="${option.value}">${additionalOptions}</div>`;
            option.parentElement.insertAdjacentHTML('afterend', additionalOptions);
        }

        const skillLevelSelect = this.container.querySelector<HTMLSelectElement>(`#${option.id}-skill-level`);
        if (skillLevelSelect) {
            // Update hidden field every time the select field changes
            skillLevelSelect.addEventListener('change', () => {
                this.updateHiddenSportsField();
            });

            // Mark as selected and initialize values, if this sport was previously selected
            if (selectedSport && selectedSport.skillLevel) {
                skillLevelSelect.value = selectedSport.skillLevel;
            }
        }

        const rentalEquipmentCheck = this.container.querySelector<HTMLInputElement>(`#${option.id}-rental-equipment`);
        if (rentalEquipmentCheck) {
            rentalEquipmentCheck.addEventListener('change', () => {
                this.updateHiddenSportsField();
            });

            if (selectedSport && selectedSport.rentalEquipment) {
                rentalEquipmentCheck.checked = selectedSport.rentalEquipment;
            }
        }

        // Initialize hidden field
        if (selectedSport) {
            option.checked = true;
        }
        this.updateHiddenSportsField();
    }

    /**
     * Deselects a sport option.
     *
     * @param {HTMLInputElement} option Sport option to deselect
     */
    private deselectSportOption(option: HTMLInputElement): void {
        // Ignore for the default "no specific sport selected" option
        if (option.value === '0') {
            // Still update the hidden field, as an empty array will hint that no actual sport is selected
            this.updateHiddenSportsField();
            return;
        }

        // Remove the container holding additional options
        this.container.querySelector(`[data-additional-sport-options][data-sport="${option.value}"]`)?.remove();

        // Again update the hidden field, to make sure that a now deselected sport is removed from there
        this.updateHiddenSportsField();
    }

    /**
     * Updates the hidden sport field with the currently selected sports, including all additional sport options.
     */
    private updateHiddenSportsField(): void {
        let defaultOptionSelected = false;
        const selectedSports: SelectedSport[] = [];

        this.sportOptions.forEach(option => {
            const title = option.dataset.title;
            if (!option.checked || !title) {
                return;
            }
            const uid = parseInt(option.value);
            if (uid === 0) {
                defaultOptionSelected = true;
                return;
            }

            // Initialize a fresh sport object
            const sport: SelectedSport = {
                uid: uid,
                title: title,
                skillLevel: null,
                rentalEquipment: null
            };

            // Set optional sport options
            const additionalSportOptions = this.container.querySelector<HTMLElement>(`[data-additional-sport-options][data-sport="${uid}"]`);
            if (additionalSportOptions) {
                const skillLevelSelect = additionalSportOptions.querySelector<HTMLSelectElement>('[data-skill-level]');
                if (skillLevelSelect) {
                    sport.skillLevel = skillLevelSelect.value;
                }
                const rentalEquipmentCheck = additionalSportOptions.querySelector<HTMLInputElement>('[data-rental-equipment]');
                if (rentalEquipmentCheck) {
                    sport.rentalEquipment = rentalEquipmentCheck.checked;
                }
            }

            selectedSports.push(sport);
        });

        // If neither default option ("no sport needed") nor any other sport is selected, completely empty the hidden field
        if (selectedSports.length === 0 && !defaultOptionSelected) {
            this.sportsField.value = '';
        } else {
            this.sportsField.value = JSON.stringify(selectedSports);
        }
    }
}
